import { forwardRef, useMemo, useRef, useState } from 'react';
import type { z } from 'zod';

import type { ReportSchema } from '@ballerine/common';
import type { CustomerFeatures } from '@ballerine/customer-schemas';
import { ctw } from '@/common/utils/ctw';
import { useDigitalFootprintReportSections } from '../../hooks/useDigitalFootprintReportSections/useDigitalFootprintReportSections';

import { useRegisterTranslationProtectedTerms } from '@/common/translation/translation-protection-store';

import { SectionObserver } from '../../../composites';
import { GatedContentCheckBar } from '../GatedContentCheckBar';

type DigitalFootprintReportProps = {
  report: z.infer<typeof ReportSchema>;
  customerFeatures?: CustomerFeatures | null;
  disableSectionObserver?: boolean;
  FeedbackComponent?: React.ComponentType<{ reportId: string }>;
  locale?: string;
  googleMapsApiKey: string;
};

export const DigitalFootprintReport = forwardRef<HTMLDivElement, DigitalFootprintReportProps>(
  (
    {
      report,
      customerFeatures,
      disableSectionObserver = false,
      FeedbackComponent,
      locale,
      googleMapsApiKey,
    },
    ref,
  ) => {
    const { sections } = useDigitalFootprintReportSections(
      report,
      customerFeatures,
      googleMapsApiKey,
      FeedbackComponent,
      locale,
    );
    const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const translationProtectedTerms = useMemo(() => {
      const terms = new Set<string>();

      const addTerm = (value?: string | null) => {
        if (value?.trim()) {
          terms.add(value.trim());
        }
      };

      addTerm(report.data?.companyName as string | undefined);
      addTerm(report.data?.merchantDetails?.companyName as string | undefined);
      addTerm(report.customer.displayName as string | undefined);

      return Array.from(terms);
    }, [report]);

    const isTranslationFeatureEnabled = Boolean(customerFeatures?.enableTranslation);
    useRegisterTranslationProtectedTerms(
      isTranslationFeatureEnabled ? translationProtectedTerms : [],
    );
    const isGatedContent = useMemo(
      () => Boolean(report?.data?.gatedContentCheck),
      [report?.data?.gatedContentCheck],
    );

    return (
      <div
        className={ctw(
          'mx-auto flex justify-center ps-2 transition-all duration-300 gap-x-8',
          isSidebarOpen ? '2xl:max-w-8xl' : '2xl:max-w-9xl',
        )}
      >
        <div className="max-w-[62.5rem] overflow-y-visible transition-all duration-300" ref={ref}>
          {isGatedContent && (
            <GatedContentCheckBar
              status={(report.data?.gatedContentCheck?.status as 'success' | 'failed') ?? 'failed'}
              message={report.data?.gatedContentCheck?.message ?? undefined}
              sessionUrl={report.data?.gatedContentCheck?.sessionUrl ?? null}
              isGatedContentCheckEnabled={
                customerFeatures?.DIGITAL_FOOTPRINT?.enableGatedContentCheck ?? false
              }
            />
          )}
          {sections.map(section => {
            return (
              <div
                key={section.id}
                id={section.id}
                ref={el => (sectionRefs.current[section.id] = el)}
                className="min-h-[100px]"
              >
                {section.Component}
              </div>
            );
          })}
        </div>

        {!disableSectionObserver && (
          <SectionObserver
            sections={sections}
            sectionRefs={sectionRefs}
            isSidebarOpen={isSidebarOpen}
            onSidebarToggle={setIsSidebarOpen}
          />
        )}
      </div>
    );
  },
);

DigitalFootprintReport.displayName = 'DigitalFootprintReport';
